import React from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';
import intelligentPlayImg from '../assets/images/features/intelligent-play.png';
import songLibraryImg from '../assets/images/features/song-library.png';
import progressTrackingImg from '../assets/images/features/progress-tracking.png';
import { Helmet } from 'react-helmet';

const HomeContainer = styled.div`
  min-height: 100vh;
  background-color: #fff;
  color: #333;
`;

const Section = styled.section`
  padding: 80px 24px;
  
  @media (min-width: 768px) {
    padding: 120px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 60px 20px;
  }
`;

const HeroSection = styled(Section)`
  text-align: center;
  background: linear-gradient(135deg, #fff 0%, #f5f9ff 100%);
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #1976d2;
  
  @media (min-width: 768px) {
    font-size: 48px;
    margin-bottom: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 20px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
  padding: 0 20px;
  
  @media (min-width: 768px) {
    font-size: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    padding: 0 12px;
  }
`;

const CTAButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  font-size: 18px;
  border-radius: 24px;
  background: #1976d2;
  color: #fff;
  border: none;
  margin-top: 32px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
  
  @media (max-width: 768px) {
    margin-top: 24px;
  }
  
  &:hover {
    background: #1565c0 !important;
    color: #fff !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.3);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 1200px;
  margin: 80px auto 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.1);
  border: 1px solid rgba(25, 118, 210, 0.1);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.15);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 16px;
  color: #1976d2;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 17px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const FeatureSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  gap: 80px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
    gap: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 32px 12px;
    gap: 60px;
  }
`;

const Feature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  gap: 48px;
  
  @media (min-width: 768px) {
    padding: 0 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 12px;
    gap: 32px;
  }
`;

const FeatureContent = styled.div`
  width: 100%;
  max-width: 600px;
  text-align: center;
  margin: 0 auto;
`;

const FeatureImage = styled.div`
  background: #ffffff;
  border-radius: 24px;
  aspect-ratio: 16/9;
  order: ${props => props.imageRight ? 2 : 1};
  overflow: hidden;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  transition: all 0.5s cubic-bezier(0.42, 0, 0.58, 1);
  box-shadow: 
    0 0 1px rgba(0, 0, 0, 0.05),
    0 1px 6px rgba(0, 0, 0, 0.05),
    0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.02);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transform: perspective(1000px) translateZ(0);
  will-change: transform, box-shadow;
  
  &:hover {
    transform: perspective(1000px) translateZ(8px) translateY(-8px);
    box-shadow: 
      0 0 1px rgba(0, 0, 0, 0.05),
      0 4px 12px rgba(0, 0, 0, 0.08),
      0 16px 32px rgba(0, 0, 0, 0.12);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 20px;
    transition: transform 0.5s cubic-bezier(0.42, 0, 0.58, 1);
    filter: contrast(1.02);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    aspect-ratio: 4/3;
    border-radius: 20px;
    
    img {
      padding: 16px;
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 16px;
    box-shadow: 
      0 0 1px rgba(0, 0, 0, 0.05),
      0 1px 4px rgba(0, 0, 0, 0.05),
      0 6px 16px rgba(0, 0, 0, 0.06);
    
    img {
      padding: 12px;
    }
    
    &:hover {
      transform: perspective(1000px) translateZ(4px) translateY(-4px);
      box-shadow: 
        0 0 1px rgba(0, 0, 0, 0.05),
        0 2px 8px rgba(0, 0, 0, 0.06),
        0 12px 24px rgba(0, 0, 0, 0.08);
    }
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px auto 0;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FeatureItem = styled.li`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
  text-align: center;
  line-height: 1.6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  
  &:before {
    content: '•';
    color: #1976d2;
    font-size: 24px;
    line-height: 1;
  }
  
  @media (min-width: 768px) {
    font-size: 17px;
    margin-bottom: 24px;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

function Home() {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;

  return (
    <>
      <Helmet>
        <link rel="icon" href="/favicon.ico" />
        <title>Kalimba Flows - Follow Cursor • Build Tabs.</title>
        <meta name="description" content="Follow Cursor • Build Tabs." />
        <meta name="keywords" content="kalimba flows, kalimba tabs app, best kalimba app, kalimba tutorial app, kalimba tabs, kalimba learning, kalimba play along, thumb piano, music learning" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Kalimba Flows - Follow Cursor • Build Tabs." />
        <meta property="og:description" content="Follow Cursor • Build Tabs." />
        <meta property="og:site_name" content="Kalimba Flows" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kalimba Flows - Follow Cursor • Build Tabs." />
        <meta name="twitter:description" content="Follow Cursor • Build Tabs." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Kalimba Flows",
              "description": "Interactive kalimba learning platform with play-along guide and tab editor, best kalimba app, kalimba tabs app, kalimba tutorial app, kalimba tabs, kalimba learning",
              "applicationCategory": "MusicEducation",
              "offers": {
                "@type": "Offer",
                "category": "Music Learning Platform"
              },
              "featureList": [
                "Interactive play-along guide",
                "Professional tab editor",
                "Extensive kalimba tab library",
                "Adjustable playback speed",
                "Cloud sync across devices"
              ]
            }
          `}
        </script>
      </Helmet>
      <HomeContainer>
        <HeroSection>
          <Title>{t.home.hero.title}</Title>
          <Subtitle>{t.home.hero.subtitle}</Subtitle>
          
          <Link to="/download">
            <CTAButton type="primary" onClick={() => navigate('/download')}>
              {t.home.hero.cta}
            </CTAButton>
          </Link>
        </HeroSection>

        <FeatureSection>
          <Feature imageRight>
            <FeatureContent imageRight>
              <FeatureTitle>{t.home.features.smartPlay.title}</FeatureTitle>
              <FeatureDescription>
                {t.home.features.smartPlay.description}
              </FeatureDescription>
              <FeatureList>
                {t.home.features.smartPlay.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeatureList>
            </FeatureContent>
            {<FeatureImage>
              <img 
                src={intelligentPlayImg} 
                alt="智能跟弹系统界面展示"
                loading="lazy"
              />
            </FeatureImage>}
          </Feature>

          <Feature imageRight>
            <FeatureContent imageRight>
              <FeatureTitle>{t.home.features.library.title}</FeatureTitle>
              <FeatureDescription>
                {t.home.features.library.description}
              </FeatureDescription>
              <FeatureList>
                {t.home.features.library.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeatureList>
            </FeatureContent>
            {<FeatureImage imageRight>
              <img 
                src={songLibraryImg} 
                alt="曲库资源展示"
                loading="lazy"
              />
            </FeatureImage>}
          </Feature>
          
          <Feature imageRight>
            <FeatureContent imageRight>
              <FeatureTitle>{t.home.features.sharing.title}</FeatureTitle>
              <FeatureDescription>
                {t.home.features.sharing.description}
              </FeatureDescription>
              <FeatureList>
                {t.home.features.sharing.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeatureList>
            </FeatureContent>
            {<FeatureImage>
              <img 
                src={progressTrackingImg} 
                alt="自制曲目分享界面"
                loading="lazy"
              />
            </FeatureImage>}
          </Feature>

          
        </FeatureSection>
      </HomeContainer>
    </>
  );
}

export default Home; 